import styled from 'styled-components';

import { FieldValues } from 'react-hook-form';

import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/hooks/redux-helpers';

import { ITemplate } from '@/store/models/template.model';

import { searchTemplateModel } from '@/store/actions/template.actions';
import { selectTemplateGroupByGroupAndName } from '@/store/selectors';
import TemplateEditor, { ITemplateProps } from '@/pages/admin/template/TemplateEditor';
import { TemplateSkeleton } from '@/pages/admin/template/TemplateSkeleton';
import { TemplatesError } from '@/pages/admin/template/TemplatesError';

const TemplateContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
`;

const TemplatePreview = styled.iframe`
  background-color: ${({ theme }) => theme.palette.white};
  border-top-right-radius: 0.4rem;
  padding: 1.3rem 2.8rem;
  border: none;
  width: 100%;
  height: 100%;
`;

export interface ITemplateViewerProperties<T extends FieldValues> {
  template?: ITemplate;
  design?: string;
  templateEditor: ITemplateProps<T>;
}

const TemplateViewer = <T extends FieldValues>({ template, templateEditor, design }: ITemplateViewerProperties<T>) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const isTemplateLoading = useAppSelector(
    (state) => template && selectTemplateGroupByGroupAndName(state, template.group, template.name)?.isLoading,
  );

  const templateError = useAppSelector(
    (state) => template && selectTemplateGroupByGroupAndName(state, template.group, template.name)?.error,
  );

  if (isTemplateLoading || !template)
    return (
      <TemplateContainer>
        <TemplateSkeleton theme="dark" borderRadius="0.4rem 0 0 0" />
        <TemplateSkeleton theme="light" borderRadius="0 0.4rem 0 0" />
      </TemplateContainer>
    );

  return templateError ? (
    <TemplatesError
      message={t('pages.administration.templates.selectTemplateError')}
      onReload={() => {
        template && dispatch(searchTemplateModel(template));
      }}
    />
  ) : (
    <TemplateContainer>
      {template && <TemplateEditor<T> {...templateEditor} />}
      {design && <TemplatePreview srcDoc={design} data-testid="template-preview" />}
    </TemplateContainer>
  );
};

export default TemplateViewer;
